import * as Yup from "yup";
import {
  hasNomineeAge,
  isOtherRelation,
  isKshema,
  isNational,
  isReliance,
  sameCompany,
  routingOf,
  hasCompanyLeg,
  nomineeFieldsOf,
  isFinancierNameTooShort,
  KSHEMA_FINANCIER_NAME_MIN_LETTERS,
  EXISTING_TP_INSURERS,
  existingTpMode,
  isTpPolicyNumber,
} from "./companyRules";
import { PROVIDER_COMPANIES } from "../../../../Shared/CommonConstant";
import { PROPOSER_TITLES } from "./proposerTitle";
import { KSHEMA_OLD_RELATIONS } from "../constants/formOptions";

/** Insurers the Online Policy can be raised for. The company itself is never
 *  chosen here — it is copied from the Make & Model master and hidden — so a
 *  value missing from this list means a master record was saved for an insurer
 *  the policy form does not support yet, and the operator must be told rather
 *  than left staring at a form that will not submit.
 *
 *  Shares PROVIDER_COMPANIES with the credential screens on purpose: a policy
 *  can only be automated for an insurer someone can store a portal login for,
 *  so the two lists drifting apart is always a bug. */
const SUPPORTED_COMPANIES = PROVIDER_COMPANIES;

// The nominee / PA rules depend on the policy's company ROUTING, not just its
// single `company`: a Reliance OD / National TP policy needs National's nominee
// rules even though `company` (the OD insurer) says Reliance. Yup's `when`
// hands over the listed siblings, so the routing inputs are named here and
// rebuilt into the same shape companyRules works on.
// `coverage` is last on purpose: PA through Brisk is Brisk's own nominee on two insurers AND on TP only (utils/companyRules.js
// isBriskNominee), and TP only is told by the coverage, not by the routing.
const ROUTING_DEPS = ["paCover", "company", "settings", "odCompany", "tpCompany", "coverage"];
const asValues = ([paCover, company, settings, odCompany, tpCompany, coverage]) => ({
  paCover,
  company,
  settings,
  odCompany,
  tpCompany,
  coverage,
});
/** Which nominee fields the PA Cover Details section asks — the same answer the form shows (nomineeFieldsOf). */
const nomineeAsks = (...deps) => nomineeFieldsOf(asValues(deps));
/** A National insurer is on either the OD or the TP side. */
const nationalLeg = (...deps) => hasCompanyLeg(asValues(deps), isNational);
// Either leg (OD or TP) on Reliance: its proposer form has a mandatory Title list.
const relianceLeg = (...deps) => hasCompanyLeg(asValues(deps), isReliance);

export const onlinePolicySchema = Yup.object().shape({
  company: Yup.string()
    .required("Company is required")
    .oneOf(
      SUPPORTED_COMPANIES,
      `Company must be one of: ${SUPPORTED_COMPANIES.join(", ")}`
    ),
  // Product Selection. Each company field is valid if EITHER it was set
  // directly OR the Make & Model-derived `company` covers it — the "OD/TP
  // Company" Autocompletes in BasicPolicyDetailsStep fall back to `company`
  // for display, so validation has to accept that same fallback rather than
  // showing a false "required" error when it's already effectively chosen.
  odSelected: Yup.boolean(),
  odCompany: Yup.string().when("odSelected", {
    is: true,
    then: (schema) =>
      schema.test(
        "od-company-required",
        "OD Company is required",
        function (value) {
          return !!(value || this.parent.company);
        }
      ),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
  tpSelected: Yup.boolean(),
  tpCompany: Yup.string().when("tpSelected", {
    is: true,
    then: (schema) =>
      schema.test(
        "tp-company-required",
        "TP Company is required",
        function (value) {
          return !!(value || this.parent.company);
        }
      ),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
  // PA Cover Company (settings.PACompany) is picked in the single dropdown and
  // written through to paCoverCompany / paCoverInsurerCompany, so the required
  // check on paCoverCompany below is the one that covers it.
  makeModel: Yup.string().required("Make & Model Required"),
  // Multiple company: a Combined Make & Model record must be chosen (it supplies
  // both companies and both vehicles). A SAVED policy that has two companies but no
  // record (sold before records existed) keeps what it was sold with and needs
  // none until its vehicle is changed.
  makeModelCombo: Yup.string().when(["companyChoice", "_legacyMultiple"], {
    is: (choice, legacy) => choice === "multiple" && !legacy,
    then: (schema) => schema.required("Combined Make & Model Required"),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
  rtoCityLocation: Yup.string().required("RTO City/Location Required"),
  // Not required only when RELIANCE's portal takes the IDV out of our hands:
  // with the vehicle flagged as missing from its list it derives the IDV from
  // the selling price, so 0 is stored here. National's operator still types an
  // IDV in that mode, so it stays required — mirrors idvLockedByPortal in
  // BasicPolicyDetailsStep.
  idv: Yup.string().when(["modelNotFound", "company", "coverage"], {
    is: (modelNotFound, company, coverage) =>
      // TP only: nothing is insured for own damage, so there is no IDV to enter.
      coverage === "tp" ||
      (modelNotFound === true &&
        String(company || "").trim().toLowerCase() !== "national"),
    then: (schema) => schema.notRequired().nullable(),
    otherwise: (schema) => schema.required("IDV Required"),
  }),
  manufacturingYear: Yup.string().required("Manufacturing Year Required"),
  manufacturingMonth: Yup.string().required("Manufacturing Month Required"),
  purchaseDate: Yup.string().required("Purchase Date Required"),
  registrationDate: Yup.string().required("Registration Date Required"),
  zeroDepreciation: Yup.boolean(),
  // Mandatory once Zero Depreciation is ticked. The value comes from the
  // Make & Model master's Nil Dip Price; when that row has none the user types
  // one here and it is saved back to the master after the policy is created.
  zeroDepreciationPercentage: Yup.number()
    .transform((value, original) =>
      original === "" || original === null ? undefined : value
    )
    .min(0, "Nil Dip Price must be 0 or greater")
    .when("zeroDepreciation", {
      is: true,
      then: (schema) => schema.required("Nil Dip Price is required"),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  // Required: the base rate differs per zone, so an empty zone would price the
  // policy at whatever the calculator falls back to.
  zone: Yup.string()
    .oneOf(["A", "B"], "Select a zone")
    .required("Zone is required"),
  tppdRestrict: Yup.boolean(),
  paCover: Yup.boolean(),
  paCoverCompany: Yup.string().when("paCover", {
    is: true,
    then: (schema) => schema.required("PA Cover Company is required"),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
  // An insurer's floor is per vehicle (the Make & Model master can raise it); Brisk's is always 177.
  // _paCoverMinAmount carries the resolved company/vehicle minimum onto the form so
  // this static schema can read it — Yup cannot see the selected make/model.
  // Required whoever sells the PA (2026-10-23, the owner: "amount is mandatory"): an insurer's PA is priced from this
  // amount too (PA to Owner Driver in Total B), so an empty one sold the cover for ₹0. It used to be Brisk only.
  // A box typed in and cleared holds "" — that is "not entered", not Yup's "must be a `number` type ... NaN".
  paCoverAmount: Yup.number()
    .transform((value, original) => (original === "" || original === null ? undefined : value))
    .typeError("Enter the PA Cover amount")
    .when("paCover", {
      is: true,
      then: (schema) =>
        schema
          .required("Enter the PA Cover amount")
          .min(
            Yup.ref("_paCoverMinAmount"),
            ({ min }) => `Minimum amount is ${min}`
          )
          .max(6500, "Maximum amount is 6500"),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  // National demands a nominee on EVERY quote, PA Cover or not — see
  // fullPolicycompany/national.js in the automation. Other insurers only ask
  // for one when PA Cover is on.
  nomineeName: Yup.string().when(ROUTING_DEPS, {
    is: (...deps) => deps[0] === true || nationalLeg(...deps),
    then: (schema) =>
      schema
        .required("Nominee Name Required")
        .matches(/^[a-zA-Z\s]+$/, "Only alphabets are allowed")
        .min(2, "Nominee Name must be at least 2 characters"),
    otherwise: (schema) => schema.notRequired(),
  }),
  nomineeGender: Yup.string().when(ROUTING_DEPS, {
    is: (...deps) => deps[0] === true && nomineeAsks(...deps).gender,
    then: (schema) => schema.required("Nominee Gender Required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  nomineeRelation: Yup.string()
    .when(ROUTING_DEPS, {
      is: (...deps) => deps[0] === true || nationalLeg(...deps),
      then: (schema) => schema.required("Nominee Relation Required"),
      otherwise: (schema) => schema.notRequired(),
    })
    // KSHEMA's PA nominee: one of KSHEMA's own choices (constants/formOptions.js kshemaNomineeRelations). A policy saved
    // with its old "Child" / "Parent" asks for a new one instead of reaching the portal with a choice it no longer has.
    .test("kshema-relation", "Choose one of KSHEMA's relations", function (value) {
      const p = this.parent || {};
      if (!value || p.paCover !== true) return true;
      const asks = nomineeFieldsOf(asValues([p.paCover, p.company, p.settings, p.odCompany, p.tpCompany, p.coverage]));
      if (asks.brisk || !isKshema(asks.company)) return true;
      const key = String(value).trim().toLowerCase();
      if (asks.relations.some((r) => r.value === key)) return true;
      const instead = KSHEMA_OLD_RELATIONS[key];
      return this.createError({
        message: instead ? `KSHEMA no longer has "${value}" - choose ${instead}` : "Choose one of KSHEMA's relations",
      });
    }),
  // KSHEMA only — the portal asks whether the nominee is a person or a company.
  nomineeType: Yup.string().when(ROUTING_DEPS, {
    is: (...deps) => deps[0] === true && nomineeAsks(...deps).type,
    then: (schema) => schema.required("Nominee Type Required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  // KSHEMA only. A single nominee row, so the share has to be a whole 1-100.
  nomineeShare: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .nullable()
    .when(ROUTING_DEPS, {
      is: (...deps) => deps[0] === true && nomineeAsks(...deps).share,
      then: (schema) =>
        schema
          .required("Nominee Share Required")
          .min(1, "Share must be at least 1%")
          .max(100, "Share cannot exceed 100%"),
      otherwise: (schema) => schema.notRequired(),
    }),
  otherRelationName: Yup.string().when([...ROUTING_DEPS, "nomineeRelation"], {
    is: (...deps) =>
      deps[0] === true &&
      nomineeAsks(...deps.slice(0, ROUTING_DEPS.length)).otherRelation &&
      isOtherRelation(deps[ROUTING_DEPS.length]),
    then: (schema) =>
      schema
        .required("Other Relation Name Required")
        .matches(/^[a-zA-Z\s]+$/, "Only alphabets are allowed")
        .min(2, "Relation Name must be at least 2 characters"),
    otherwise: (schema) => schema.notRequired(),
  }),
  // Not on KSHEMA's proposal — requiring it there would block a submission on
  // a field its portal never asks for.
  appointeeName: Yup.string().when(ROUTING_DEPS, {
    is: (...deps) => deps[0] === true && nomineeAsks(...deps).appointee,
    then: (schema) =>
      schema
        .required("Appointee Name Required")
        .matches(/^[a-zA-Z\s]+$/, "Only alphabets are allowed")
        .min(2, "Appointee Name must be at least 2 characters"),
    otherwise: (schema) => schema.notRequired(),
  }),
  nomineeDob: Yup.date().when(ROUTING_DEPS, {
    is: (...deps) => deps[0] === true && nomineeAsks(...deps).dob,
    then: (schema) =>
      schema
        .required("Nominee Date of Birth Required")
        // 18 or older (the owner, 2026-11-05: "nominee date of birth 18 want to mandatory, not remove that" - kept after
        // it was briefly removed the same day).
        .test("age", "Nominee must be at least 18 years old", function (value) {
          if (!value) return false;
          const cutoff = new Date();
          cutoff.setFullYear(cutoff.getFullYear() - 18);
          return value <= cutoff;
        }),
    otherwise: (schema) => schema.notRequired(),
  }),
  nomineeAge: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .nullable()
    .when(ROUTING_DEPS, {
      // hasNomineeAge is National-only — required regardless of PA Cover,
      // same as nomineeName/nomineeRelation above, and whichever side (OD or
      // TP) National is on.
      is: (...deps) => nationalLeg(...deps) && hasNomineeAge("National"),
      then: (schema) =>
        schema
          .required("Nominee Age Required")
          .min(18, "Nominee must be at least 18 years old")
          .max(120, "Invalid Age"),
      otherwise: (schema) => schema.notRequired(),
    }),
  paCoverYears: Yup.number()
    // "" (nothing picked yet) is "missing", not a type error.
    .transform((value, originalValue) => (originalValue === "" || originalValue === null ? null : value))
    .nullable()
    .when(ROUTING_DEPS, {
      is: (...deps) => deps[0] === true && nomineeAsks(...deps).years,
      then: (schema) => schema.required("Years required"),
      otherwise: (schema) => schema.notRequired(),
    })
    // A National TP (Multiple company, or a single-company TP only) asks the PA term ONLY when PA Cover is ticked and
    // bought from National: no default, it has to be 1 or 5 AND picked on purpose (`_paYearsChosen`, set by the
    // picker, or by a saved policy being edited). With PA Cover off the portal gets "No", and with PA Cover from Brisk
    // it stays "No" (Brisk's certificate is the cover) — nothing is asked either way.
    .test("national-tp-years", "Select the PA term: 1 year or 5 years", function (value) {
      const p = this.parent || {};
      if (p.companyChoice !== "multiple" && p.coverage !== "tp") return true;
      if (p.paCover !== true) return true;
      const routing = routingOf({
        settings: p.settings,
        odCompany: p.odCompany,
        tpCompany: p.tpCompany,
        company: p.company,
      });
      if (!isNational(routing.TPCompany) || !isNational(routing.PACompany)) return true;
      return p._paYearsChosen === true && (value === 1 || value === 5);
    }),
  Paymentmethod: Yup.string(),
  notPaCoverReason: Yup.string().when(ROUTING_DEPS, {
    // A KSHEMA policy (on either side) that is NOT buying PA from KSHEMA has to
    // say why the insurer's own PA was declined.
    is: (...deps) => {
      const values = asValues(deps);
      return (
        hasCompanyLeg(values, isKshema) &&
        !(deps[0] === true && sameCompany(routingOf(values).PACompany, "KSHEMA"))
      );
    },
    then: (schema) => schema.required("Reason for unselecting is required"),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
});

export const customerDetailsSchema = Yup.object().shape({
  // Customer Details
  fullName: Yup.string().required("Full Name is required"),
  surname: Yup.string().required("Surname is required"),
  dateOfBirth: Yup.date()
    .required("Date of Birth is required")
    .test("age", "Customer must be at least 18 years old", function (value) {
      if (!value) return false;
      const cutoff = new Date();
      cutoff.setFullYear(cutoff.getFullYear() - 18);
      return value <= cutoff;
    }),
  gender: Yup.string().required("Gender is required"),
  // Reliance's proposer form makes the Title mandatory, so a policy on Reliance (either leg) must have one. For the
  // other insurers it stays optional: empty falls back to what the Gender implies (utils/proposerTitle.js titleToSend).
  proposerTitle: Yup.string()
    // A custom test, not oneOf: oneOf would reject "" before `required` could say why.
    .test("listed-title", "Choose one of the listed titles", (value) => !value || PROPOSER_TITLES.includes(value))
    .when(ROUTING_DEPS, {
      is: (...deps) => relianceLeg(...deps),
      then: (schema) => schema.required("Title is required for Reliance"),
      otherwise: (schema) => schema.notRequired(),
    }),
  fatherName: Yup.string().required("Father's Name is required"),

  // Address Details
  flatDoorNo: Yup.string().required("Flat/Room No is required"),
  floorNo: Yup.string().required("Floor No is required"),
  buildingName: Yup.string().required("Name of Premises is required"),
  blockName: Yup.string().required("Block Name/No is required"),
  roadStreetLane: Yup.string().required("Road/Street/Lane is required"),
  areaAndLocality: Yup.string().required("Area and Locality is required"),
  state: Yup.string().required("State is required"),
  country: Yup.string().required("Country is required"),
  pincode: Yup.string()
    .required("Pincode is required")
    .matches(/^[0-9]{6}$/, "Pincode must be 6 digits"),
  email: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),
  mobileNumber: Yup.string()
    .required("Mobile Number is required")
    .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits"),

  // Vehicle Details
  engineNumber: Yup.string().required("Engine Number is required"),
  chassisNumber: Yup.string().required("Chassis Number is required"),

  // File Uploads
  panCardFront: Yup.mixed().notRequired(),
  panCardBack: Yup.mixed().notRequired(),
  aadharCardFront: Yup.mixed().notRequired(),
  aadharCardBack: Yup.mixed().notRequired(),

  // Filled by OCR, never typed. Validated as a shape check only — the "you
  // must re-upload" block lives in FormNavigationButtons, which can tell a
  // new policy from an edit of one saved before this field existed.
  aadharNumber: Yup.string()
    .notRequired()
    .test(
      "aadhar-digits",
      "Aadhaar card number is not clear. Please upload a clearer image.",
      (value) => !value || /^\d{12}$/.test(value)
    ),

  // Financier Details
  // .trim(): a name of spaces only is not a name (it used to pass "required" and reach the portal empty). Formik validates a
  // trimmed COPY — what the operator typed is left as it is.
  financierName: Yup.string()
    .trim()
    .when("hasFinancier", {
      is: true,
      then: (schema) => schema.required("Financier Name is required"),
      otherwise: (schema) => schema.notRequired(),
    })
    // KSHEMA's Financier Name is a search dropdown that does not open for one character (either insurer of the policy being
    // KSHEMA sends this name there). An empty name is the "required" rule's message, not this one.
    .test(
      "kshema-financier-name-length",
      `Financier Name must be at least ${KSHEMA_FINANCIER_NAME_MIN_LETTERS} letters (KSHEMA's financier list does not open for one)`,
      function (value) {
        const p = this.parent || {};
        if (p.hasFinancier !== true || !isFinancierNameTooShort(value)) return true;
        const kshemaLeg = hasCompanyLeg(asValues([p.paCover, p.company, p.settings, p.odCompany, p.tpCompany]), isKshema);
        return !kshemaLeg;
      }
    ),
});

/**
 * The customer's existing TP policy (CustomerDetailsStep "Third party (TP) policy"). OD only: all four required.
 * Others, two insurers included: not asked on the form (entered once the TP part is finished).
 */
const tpMode = (ctx) => existingTpMode(ctx.parent || {});
const tpDay = (v) => {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};
export const existingTpSchema = Yup.object().shape({
  existingTpInsurer: Yup.string()
    .nullable()
    .test("tp-insurer-required", "Choose the TP insurer", function (value) {
      return tpMode(this) !== "required" || !!String(value || "").trim();
    })
    .test("tp-insurer-listed", `The TP insurer must be ${EXISTING_TP_INSURERS.join(" or ")}`, function (value) {
      return tpMode(this) !== "required" || !value || EXISTING_TP_INSURERS.includes(value);
    }),
  existingTpPolicyNumber: Yup.string()
    .nullable()
    .test("tp-number-required", "TP Policy Number is required", function (value) {
      return tpMode(this) !== "required" || !!String(value || "").trim();
    })
    .test("tp-number-shape", "The TP policy number looks wrong. Use letters, digits and / - only", function (value) {
      return !tpMode(this) || !String(value || "").trim() || isTpPolicyNumber(value);
    }),
  existingTpStartDate: Yup.mixed()
    .nullable()
    .test("tp-start-required", "TP Start Date is required", function (value) {
      return tpMode(this) !== "required" || tpDay(value) !== null;
    }),
  existingTpEndDate: Yup.mixed()
    .nullable()
    .test("tp-end-required", "TP End Date is required", function (value) {
      return tpMode(this) !== "required" || tpDay(value) !== null;
    })
    .test("tp-end-after-start", "The TP end date must be after the TP start date", function (value) {
      if (!tpMode(this)) return true;
      const start = tpDay(this.parent.existingTpStartDate);
      const end = tpDay(value);
      return start === null || end === null || end > start;
    }),
});

export const combinedValidationSchema = Yup.object().shape({
  ...onlinePolicySchema.fields,
  ...customerDetailsSchema.fields,
  ...existingTpSchema.fields,
});
